import { Stack } from "@mui/material";
import useAppStore from "App/hooks/useAppStore";
import ActionButton from "components/Buttons/ActionButton";
import useNotifications from "features/Notifications/hooks/useNotifications";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavigateOptions, To, useNavigate } from "react-router-dom";
import { LOGIN_PAGE } from "utils/axiosInterceptors";

interface Params {
  blockNavigateMessage?: string;
  onLeave?: () => void;
  forceNavigate?: boolean;
}

interface VidletNavigateOptions {
  forceNavigate?: boolean;
}

export default function useVidletNavigate({
  blockNavigateMessage: _blockNavigateMessage,
  onLeave,
}: Params = {}) {
  const blockNavigateMessage =
    _blockNavigateMessage || "You have unsaved changes!";
  const navigateParams = useRef<
    [to: To, options?: NavigateOptions] | undefined
  >(undefined);
  const navigate = useNavigate();
  const { canNavigate, setCanNavigate } = useAppStore();
  const { resetNotification, setNotification } = useNotifications();
  const [pendingLoginRedirect, setPendingLoginRedirect] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!pendingLoginRedirect) return;
    setCanNavigate(true);
    window.location.href = pendingLoginRedirect;
    setPendingLoginRedirect(null);
  }, [pendingLoginRedirect, setCanNavigate]);

  const handleBeforeUnload = useCallback(
    (event) => {
      if (!canNavigate) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    [canNavigate],
  );

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [handleBeforeUnload]);

  const showBlocker = () => {
    setNotification({
      autoHideDuration: null,
      message: blockNavigateMessage,
      severity: "warning",
      onClose: undefined,
      action: (
        <Stack direction="row" spacing={2}>
          <ActionButton
            onClick={() => {
              resetNotification();
              setCanNavigate(true);
              onLeave && onLeave();
              navigateParams.current && navigate(...navigateParams.current);
              navigateParams.current = undefined;
            }}
          >
            Leave
          </ActionButton>
          <ActionButton
            onClick={() => {
              navigateParams.current = undefined;
              resetNotification();
            }}
          >
            Stay
          </ActionButton>
        </Stack>
      ),
    });
  };

  function forceNavigate(
    ...params: [to: To, options?: NavigateOptions & VidletNavigateOptions]
  ) {
    const isLogin = params[0] === "login";
    if (isLogin) {
      setPendingLoginRedirect(
        `${LOGIN_PAGE}?redirect_url=${window.location.pathname}`,
      );
      return;
    }
    setCanNavigate(true);
    navigate(...params);
  }

  return {
    navigate: (
      ...params: [to: To, options?: NavigateOptions & VidletNavigateOptions]
    ) => {
      if (canNavigate || params[1]?.forceNavigate) {
        forceNavigate(...params);
      } else {
        navigateParams.current = params;
        showBlocker();
      }
    },
    forceNavigate,
    setCanNavigate,
  };
}
